import React, { useState, useEffect } from "react";
import { Box, Typography, Button, IconButton, Dialog, DialogTitle, DialogContent } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import AppsIcon from '@icons/AppsIcon';
import BallotIcon from '@icons/BallotIcon';
import ArrowLeftIcon from '@icons/ArrowLeftIcon';

import discoveryData from '../../data/discoveryData.json';
import { sendConversation, ArticleSource } from '@services/discover';
import { getCurrentUserId } from '@utils/userUtils';
import CmsSourceLink from './CmsSourceLink';

interface Article {
  id: string;
  headline: string;
  bodyText: string;
  publishedTime: string;
  image: string;
  source_url?: string;
  sources: string[];
  categories: string[];
  featured: boolean;
  likes: number;
  isLiked: boolean;
  fullContent?: {
    introduction: string;
    sections: Array<{
      title: string;
      content: string;
    }>;
  };
  suggested_questions?: string[];
}

const ArticleDetailContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#fdffff',
  borderRadius: '12px',
  minHeight: '99vh',
  height: 'auto',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
  [theme.breakpoints.down('md')]: {
    borderRadius: '0',
    minHeight: '99vh',
  },
}));

const Header = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(253, 255, 255, 0.2)',
  borderBottom: '1px solid #35d7e4',
  padding: '0px 64px 24px',
  display: 'flex',
  alignItems: 'end',
  justifyContent: 'space-between',
  minHeight: '80px',
  position: 'sticky',
  top: 0,
  zIndex: 10,
  [theme.breakpoints.down('md')]: {
    padding: '0px 24px 12px',
    flexDirection: 'column',
    gap: '12px',
    alignItems: 'flex-start',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '0px 16px 12px',
  },
}));

const HeaderLeft = styled(Box)(({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

const HeaderTitle = styled(Typography)(({
  fontSize: '28px',
  fontWeight: 500,
  color: '#262626',
  letterSpacing: '-1px',
  lineHeight: 1.5,
}));

const FilterTabs = styled(Box)(({
  display: 'flex',
  gap: '8px',
}));

const ContentArea = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: '16px 48px',
  overflowY: 'auto',
  display: 'flex',
  gap: '16px',
  [theme.breakpoints.down('md')]: {
    padding: '16px 24px',
    flexDirection: 'column',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '16px 16px',
  },
}));

const MainContent = styled(Box)(({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
}));

const Sidebar = styled(Box)(({ theme }) => ({
  width: '340px',
  flexShrink: 0,
  alignSelf: 'flex-start',
  position: 'sticky',
  top: '-10px',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    position: 'static',
    order: -1,
  },
}));

const ArticleCard = styled(Box)(({
  backgroundColor: '#fbfeff',
  border: '1px solid #b9e3ed',
  borderRadius: '8px',
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  height: 'fit-content',
}));

const ArticleHeader = styled(Typography)(({
  fontSize: '24px',
  fontWeight: 600,
  lineHeight: 1.5,
  letterSpacing: '-0.4px',
  color: '#000',
  marginBottom: '16px',
}));

const SectionTitle = styled(Typography)(({
  fontSize: '24px',
  fontWeight: 600,
  lineHeight: 1.5,
  letterSpacing: '-0.4px',
  color: '#000',
  marginBottom: '16px',
  marginTop: '32px',
}));

const ArticleTime = styled(Box)(({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  marginBottom: '16px',
}));

const ArticleBody = styled(Typography)(({
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: 1.5,
  letterSpacing: '-0.4px',
  color: '#3b3b3b',
  marginBottom: '16px',
}));

const SourcesSection = styled(Box)(({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '16px',
  cursor: 'pointer',
}));

const SourceLogos = styled(Box)(({
  display: 'flex',
  alignItems: 'center',
  paddingRight: '6px',
}));

const SourceLogo = styled(Box)(({
  width: '16px',
  height: '16px',
  borderRadius: '50%',
  backgroundColor: '#edecec',
  marginRight: '-6px',
  border: '1px solid #fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '8px',
  fontWeight: 600,
  color: '#666',
}));

// Note: previous floating action buttons (bookmark/share/help) are currently not used.

const BackButton = styled(Button)(({
  minWidth: '32px',
  width: '32px',
  height: '32px',
  borderRadius: '6px',
  backgroundColor: 'transparent',
  color: '#666',
  marginRight: '8px',
  '&:hover': {
    backgroundColor: '#f0f0f0',
  },
}));

const TableOfContents = styled(Box)(({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#f8feff',
  border: '1px solid #c8edf5',
  borderRadius: '10px',
  overflow: 'hidden',
}));

const TOCHeader = styled(Box)(({
  padding: '12px 16px',
  borderBottom: '1px solid #c8edf5',
  backgroundColor: '#e6f9fc',
}));

const TOCItem = styled(Box)<{ active?: boolean; isTitle?: boolean }>(({ active, isTitle }) => ({
  padding: isTitle ? '12px 16px' : '9px 16px 9px 20px',
  borderLeft: active ? '3px solid #0a829d' : '3px solid transparent',
  backgroundColor: active ? '#e6f9fc' : 'transparent',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  borderBottom: '1px solid #eef9fc',
  '&:last-child': {
    borderBottom: 'none',
  },
  '&:hover': {
    backgroundColor: active ? '#d4f3fa' : '#f0fbfd',
    borderLeft: `3px solid ${active ? '#065b70' : '#5bbfd6'}`,
  },
}));

const TOCText = styled(Typography)<{ active?: boolean; isTitle?: boolean }>(({ active, isTitle }) => ({
  fontSize: isTitle ? '13px' : '12px',
  fontWeight: isTitle ? 600 : active ? 500 : 400,
  lineHeight: 1.4,
  color: active ? '#0a829d' : isTitle ? '#1a1a1a' : '#4b5563',
  letterSpacing: '-0.2px',
  transition: 'color 0.2s ease',
}));

const SuggestedQuestionsContainer = styled(Box)({
  /*marginTop: '16px',*/
  paddingTop: '12px',
  borderTop: '1px solid #e5f3f7',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '8px',
});

const SuggestedQuestionsHeader = styled(Typography)({
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: '#0a829d',
});

const SuggestedQuestionsList = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  maxWidth: '100%',
});

const SuggestedQuestionChip = styled(Button)({
  backgroundColor: '#fffefe',
  border: '1px solid #99d4e1',
  borderRadius: '20px',
  padding: '6px 14px',
  fontSize: '13px',
  fontWeight: 400,
  color: '#0a829d',
  textTransform: 'none',
  lineHeight: 1.4,
  boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.08)',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  '&:hover': {
    backgroundColor: '#e6f9fc',
    borderColor: '#0a829d',
  },
});

const QuestionInput = styled(Box)({
  position: 'relative',
  backgroundColor: '#fffefe',
  border: '1px solid #99d4e1',
  borderRadius: '12px',
  boxShadow: '0px 6px 25px 0px #e0f9ff',
  padding: '8px 8px 8px 8px',
  display: 'flex',
  alignItems: 'center',
  /*gap: '12px',*/
  marginTop: '12px',
  width: '100%',
  boxSizing: 'border-box',
});

const SearchIconWrapper = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  color: '#0a829d',
  flexShrink: 0,
});

const QuestionInputField = styled('input')({
  flex: 1,
  border: 'none',
  outline: 'none',
  fontSize: '14px',
  color: '#262626',
  backgroundColor: 'transparent',
  padding: '8px 12px',
  '&::placeholder': {
    color: '#9ca3af',
  },
});

const SendButton = styled(Button)({
  minWidth: '40px',
  width: '40px',
  height: '40px',
  borderRadius: '8px',
  backgroundColor: '#0a829d',
  color: '#fff',
  boxShadow: '0px 4px 8.3px 0px rgba(98, 98, 98, 0.4)',
  '&:hover': {
    backgroundColor: '#065b70',
  },
});


export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedCategory: string | undefined = (location.state as any)?.category;
  const getCategoryLabelById = (categoryId?: string): string | null => {
    if (!categoryId) return null;
    if (categoryId === "latest-updates") return "Regulatory Updates";
    if (categoryId === "all-sources" || categoryId === "all-updates") return "All Sources";
    if (categoryId === "my-sources") return "My Sources";
    const found = discoveryData.categories.find(c => c.id === categoryId);
    return found?.label ?? null;
  };
  const [article, setArticle] = useState<Article | null>(null);
  const [question, setQuestion] = useState('');
  const [activeTOCItem, setActiveTOCItem] = useState(0);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [conversationError, setConversationError] = useState<string | null>(null);
  const [isSourcesOpen, setIsSourcesOpen] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
    // First check if article was passed via navigation state (for API articles)
    const stateArticle = (location.state as any)?.article;
    if (stateArticle) {
      setArticle(stateArticle);
      return;
    }
    // Fall back to hardcoded discoveryData for local articles
    const foundArticle = discoveryData.articles.find(a => a.id === id);
    if (foundArticle) {
      setArticle(foundArticle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Helper: extract name and url from a source (string or object)
  const resolveSource = (source: string | ArticleSource): { name: string; url: string | null } => {
    if (typeof source === 'object' && source !== null) {
      return { name: source.name, url: source.url || null };
    }
    // Plain string: check if it's a URL
    if (source.startsWith('http://') || source.startsWith('https://')) {
      try {
        return { name: new URL(source).hostname.replace('www.', ''), url: source };
      } catch {
        return { name: source, url: source };
      }
    }
    // Short ID: look up in local data
    const local = discoveryData.sources.find((s: any) => s.id === source);
    return { name: local?.name || source, url: null };
  };

  // Get the URL to use for conversation (must match the URL this article was scraped from):
  // 1. article.source_url (from discover API – each article has the URL it came from)
  // 2. location.state.url (original discover URL when single URL)
  // 3. url from first source object
  const getConversationUrl = (): string | null => {
    const articleSourceUrl = article?.source_url?.trim();
    if (articleSourceUrl && (articleSourceUrl.startsWith('http://') || articleSourceUrl.startsWith('https://'))) {
      return articleSourceUrl;
    }
    const stateUrl = (location.state as any)?.url;
    if (stateUrl && (stateUrl.startsWith('http://') || stateUrl.startsWith('https://'))) {
      return stateUrl;
    }
    const sources = article?.sources || [];
    if (sources.length > 0) {
      const { url } = resolveSource(sources[0]);
      if (url) return url;
    }
    return null;
  };

  const handleSendQuestion = async (questionOverride?: string) => {
    const questionText = (questionOverride ?? question).trim();
    if (!questionText || !article) return;

    setConversationError(null);
    setConversationLoading(true);

    const userId = getCurrentUserId();
    if (userId === "0") {
      setConversationError('User not authenticated');
      setConversationLoading(false);
      return;
    }

    const sourceUrl = getConversationUrl();
    if (!sourceUrl) {
      setConversationError('No source URL available for this article');
      setConversationLoading(false);
      return;
    }

    try {
      const response = await sendConversation({
        user_id: parseInt(userId, 10),
        industry_type: 'healthcare',
        query: questionText,
        url: sourceUrl,
        conversation_id: null,
      });

      // Keep the input in sync with what was asked
      setQuestion(questionText);

      const state = location.state as any;

      navigate('/dashboard/discover/chat', {
        state: {
          linkUrl: sourceUrl,
          linkName: article.headline,
          linkId: article.id,
          fromArticle: true,
          // Pass full article + discover context so back-navigation restores correctly
          article: article,
          category: state?.category,
          url: state?.url || sourceUrl,
          discoverLinkName: state?.linkName,
          initialQuestion: questionText,
          conversationData: response,
          conversationId: response.conversation_id ?? null,
        }
      });
    } catch (error: any) {
      setConversationError(error?.message || 'Failed to get a response. Please try again.');
    } finally {
      setConversationLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSendQuestion();
    }
  };

  const renderSourceLogos = (sources: (string | ArticleSource)[]) => {
    return (
      <SourceLogos>
        {sources.map((source, index) => {
          const { name, url } = resolveSource(source);
          const initials = name.substring(0, 2).toUpperCase();
          const key = typeof source === 'string' ? source : source.url || source.name;
          const content = (
            <SourceLogo key={key} style={{ zIndex: sources.length - index }}>
              {initials}
            </SourceLogo>
          );
          return url ? (
            <CmsSourceLink
              key={`${key}-link`}
              url={url}
              title={name}
              style={{ textDecoration: 'none' }}
            >
              {content}
            </CmsSourceLink>
          ) : content;
        })}
      </SourceLogos>
    );
  };

  const tableOfContentsItems = article?.fullContent ? [
    { title: article.headline, active: activeTOCItem === 0, sectionId: 'section-intro', isTitle: true },
    ...article.fullContent.sections.map((section, index) => ({
      title: section.title,
      active: activeTOCItem === index + 1,
      sectionId: `section-${index + 1}`,
      isTitle: false,
    }))
  ] : [
    { title: article?.headline || '', active: true, sectionId: 'section-intro', isTitle: true }
  ];

  const handleTOCClick = (index: number, sectionId: string) => {
    setActiveTOCItem(index);
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (!article) {
    return (
      <ArticleDetailContainer>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography>Article not found</Typography>
          <Button onClick={() => navigate('/dashboard/discover')} sx={{ mt: 2 }}>
            Back to Discovery
          </Button>
        </Box>
      </ArticleDetailContainer>
    );
  }

  return (
    <ArticleDetailContainer>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => {
            const state = location.state as any;
            navigate('/dashboard/discover', {
              state: {
                category: state?.category,
                url: state?.url,
                linkName: state?.linkName,
                categories: state?.categories || null,
                cachedArticles: state?.cachedArticles || null,
                cachedInterests: state?.cachedInterests || null,
              }
            });
          }}>
            <ArrowLeftIcon size={16} />
          </BackButton>
          <BallotIcon size={28} color="#666" />
          <HeaderTitle>
            Discover {getCategoryLabelById(selectedCategory) ? `- ${getCategoryLabelById(selectedCategory)}` : ''}
          </HeaderTitle>
        </HeaderLeft>
        
        <FilterTabs>
          {/* Filter tabs can be shown here if needed */}
        </FilterTabs>
      </Header>

      {/* <ActionButtons>
        <RoundActionButton>
          <BookmarkIcon />
        </RoundActionButton>
        <RoundActionButton>
          <ShareIcon />
        </RoundActionButton>
        <RoundActionButton>
          <HelpIcon />
        </RoundActionButton>
      </ActionButtons> */}

      <ContentArea>
        <MainContent>
          <ArticleCard>
            <Box id="section-intro">
              <ArticleHeader>{article.headline}</ArticleHeader>
              
              <ArticleTime>
                <AppsIcon size={16} color="#666" />
                <Typography sx={{ fontSize: '12px', color: '#3b3b3b' }}>
                  {article.publishedTime}
                </Typography>
              </ArticleTime>

              {article.fullContent ? (
                <ArticleBody style={{ whiteSpace: 'pre-line' }}>
                  {article.fullContent.introduction}
                </ArticleBody>
              ) : (
                <ArticleBody>
                  {article.bodyText}
                </ArticleBody>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 2 }}>
              <SourcesSection onClick={() => setIsSourcesOpen(true)} title="View sources">
                {renderSourceLogos(article.sources)}
                <Box>
                  <Typography sx={{ fontSize: '14px', color: '#3b3b3b' }}>
                    {article.sources.length} sources
                  </Typography>
                </Box>
              </SourcesSection>
            </Box>

            {article.fullContent && article.fullContent.sections.map((section, index) => (
              <Box key={index} id={`section-${index + 1}`} sx={{ mb: 4, scrollMarginTop: '16px' }}>
                <SectionTitle>{section.title}</SectionTitle>
                <ArticleBody style={{ whiteSpace: 'pre-line' }}>
                  {section.content}
                </ArticleBody>
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mt: 2 }}>
                  <SourcesSection onClick={() => setIsSourcesOpen(true)} title="View sources">
                    {renderSourceLogos(article.sources)}
                    <Box>
                      <Typography sx={{ fontSize: '14px', color: '#3b3b3b' }}>
                        {article.sources.length} sources
                      </Typography>
                    </Box>
                  </SourcesSection>
                </Box>
              </Box>
            ))}

            {article.image && !imgFailed && (
              <img
                src={article.image}
                alt={article.headline}
                onError={() => setImgFailed(true)}
                style={{ width: '100%', height: '380px', objectFit: 'contain', marginTop: '16px', display: 'block' }}
              />
            )}
          </ArticleCard>
        </MainContent>

        <Sidebar>
          {/* On this page section - hidden on article page per design; code kept for potential future use */}
          <Box sx={{ display: 'none' }}>
            <TableOfContents>
              <TOCHeader>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#0a829d', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                  On this page
                </Typography>
              </TOCHeader>
              {tableOfContentsItems.map((item, index) => (
              <TOCItem
                key={index}
                active={item.active}
                isTitle={item.isTitle}
                onClick={() => handleTOCClick(index, item.sectionId)}
              >
                <TOCText active={item.active} isTitle={item.isTitle}>
                  {!item.isTitle && (
                    <Box component="span" sx={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', backgroundColor: item.active ? '#0a829d' : '#9ca3af', mr: '8px', mb: '1px', verticalAlign: 'middle', flexShrink: 0 }} />
                  )}
                  {item.title}
                </TOCText>
              </TOCItem>
            ))}
          </TableOfContents>
          </Box>

          <SuggestedQuestionsContainer>
            <SuggestedQuestionsHeader>
              ASK AI
            </SuggestedQuestionsHeader>
            {article?.suggested_questions && article.suggested_questions.length > 0 && (
              <SuggestedQuestionsList>
                {article.suggested_questions.map((q, idx) => (
                  <SuggestedQuestionChip
                    key={idx}
                    onClick={() => {
                      setQuestion(q);
                      handleSendQuestion(q);
                    }}
                  >
                    {q}
                  </SuggestedQuestionChip>
                ))}
              </SuggestedQuestionsList>
            )}
            <QuestionInput>
            <SearchIconWrapper>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.75 15C12.2018 15 15 12.2018 15 8.75C15 5.29822 12.2018 2.5 8.75 2.5C5.29822 2.5 2.5 5.29822 2.5 8.75C2.5 12.2018 5.29822 15 8.75 15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M17.5 17.5L13.875 13.875" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </SearchIconWrapper>
            <QuestionInputField
              type="text"
              placeholder="Ask a question about this article..."
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <SendButton onClick={() => handleSendQuestion()} disabled={conversationLoading || !question.trim()}>
              {conversationLoading ? (
                <svg width="20" height="20" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="25" cy="25" r="20" stroke="white" strokeWidth="5" strokeLinecap="round" strokeDasharray="31.415, 31.415">
                    <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
                  </circle>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.69362 10.0002L0.299335 1.27199C0.0630953 0.664515 0.655438 0.0835794 1.2414 0.290074L1.33375 0.329333L19.3337 9.32933C19.852 9.58845 19.8844 10.298 19.4309 10.6134L19.3337 10.671L1.33375 19.671C0.750768 19.9625 0.117464 19.4265 0.268798 18.8239L0.299335 18.7283L3.69362 10.0002L0.299335 1.27199L3.69362 10.0002ZM2.4021 2.54056L5.01109 9.24956L11.6387 9.25015C12.0184 9.25015 12.3322 9.53231 12.3818 9.89838L12.3887 10.0002C12.3887 10.3798 12.1065 10.6936 11.7404 10.7433L11.6387 10.7502L5.01109 10.7496L2.4021 17.4597L17.3213 10.0002L2.4021 2.54056Z" fill="white"/>
                </svg>
              )}
            </SendButton>
          </QuestionInput>
          </SuggestedQuestionsContainer>

          {conversationError && (
            <Box
              sx={{
                mt: '8px',
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                padding: '12px 16px',
                color: '#dc2626',
                fontSize: '14px',
                fontFamily: '"Rethink Sans"',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <span>{conversationError}</span>
              <IconButton
                size="small"
                onClick={() => setConversationError(null)}
                sx={{
                  marginLeft: '8px',
                  backgroundColor: '#fee2e2',
                  color: '#b91c1c',
                  '&:hover': { backgroundColor: '#fecaca' },
                }}
              >
                ×
              </IconButton>
            </Box>
          )}
        </Sidebar>
      </ContentArea>


      {/* Sources Modal */}
      <Dialog
        open={isSourcesOpen}
        onClose={(event, reason) => {
          // Prevent closing on backdrop click or escape key
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          setIsSourcesOpen(false);
        }}
        fullWidth
        maxWidth="sm"
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.4)",
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Sources</span>
            <IconButton onClick={() => setIsSourcesOpen(false)} aria-label="Close sources">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {article.sources.map((source, idx) => {
              const { name, url } = resolveSource(source);
              const key = typeof source === 'string' ? source : ((source as ArticleSource).url || (source as ArticleSource).name) + idx;
              return (
                <Box key={key} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>{name}</Typography>
                  {url ? (
                    <CmsSourceLink
                      url={url}
                      style={{ color: '#0a829D', fontSize: '12px', wordBreak: 'break-all' }}
                    >
                      {url}
                    </CmsSourceLink>
                  ) : (
                    <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>Link not available</Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </DialogContent>
      </Dialog>
    </ArticleDetailContainer>
  );
}